// ABOUTME: HR Dashboard component for admin users
// ABOUTME: Provides overview of all referrals, job roles, and user management

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useReferrals } from '@/hooks/useReferrals';
import { useJobRoles } from '@/hooks/useJobRoles';
import { TrendingUp, ArrowUp } from 'lucide-react';
interface HRDashboardProps {
  onViewChange: (view: string) => void;
  filters?: {
    jobRoleId?: string;
    jobRoleTitle?: string;
  };
}
const HRDashboard = ({
  onViewChange,
  filters
}: HRDashboardProps) => {
  const navigate = useNavigate();
  const {
    referrals
  } = useReferrals();
  const {
    jobRoles
  } = useJobRoles();

  // Debug logging
  console.log('HR Dashboard - Referrals:', referrals);
  console.log('HR Dashboard - Job Roles:', jobRoles);
  console.log('HR Dashboard - Filters:', filters);

  // Filter referrals if jobRoleId is provided
  const filteredReferrals = filters?.jobRoleId ? referrals?.filter(ref => ref.job_role_id === filters.jobRoleId) : referrals;

  // Calculate metrics
  const totalCandidates = filteredReferrals?.length || 0;
  const openRoles = jobRoles?.filter(role => role.is_active)?.length || 0;
  const activeReferrals = filteredReferrals?.filter(ref => ['submitted', 'under_review', 'interview_scheduled', 'interviewed'].includes(ref.status || ''))?.length || 0;
  const commissionsPaid = filteredReferrals?.reduce((sum, ref) => sum + (ref.bonus_paid ? ref.bonus_amount || 0 : 0), 0) || 0;

  // Calculate real progress data based on referral counts from job roles data
  const roleProgress = jobRoles?.slice(0, 4).map((role) => {
    const roleReferrals = role.total_referrals || 0;
    const target = 50; // Could be made configurable per role
    const percentage = Math.min(roleReferrals / target * 100, 100);
    return {
      id: role.id,
      title: role.title,
      applications: roleReferrals,
      percentage: Math.round(percentage)
    };
  }) || [];

  // Generate recent activity from actual referral data
  const recentActivity = filteredReferrals?.slice(0, 5).map((referral, index) => ({
    id: referral.id,
    message: `New referral submitted for ${referral.job_roles?.title || 'Unknown Role'} by ${referral.candidate_name}`,
    time: new Date(referral.submitted_at || '').toLocaleDateString(),
    type: "referral",
    status: referral.status || "submitted"
  })) || [];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-red-500';
      case 'scheduled':
        return 'bg-orange-500';
      case 'completed':
        return 'bg-green-500';
      case 'approved':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {filters?.jobRoleTitle ? `${filters.jobRoleTitle} - Referrals` : 'HR Dashboard'}
          </h1>
          {filters?.jobRoleTitle && <Button variant="outline" onClick={() => navigate('/hr')} className="mt-2">
              ← Back to All Referrals
            </Button>}
        </div>
      </div>

      {/* Top Metrics Bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  {filters?.jobRoleTitle ? 'Role Candidates' : 'Total Candidates'}
                </p>
                <p className="text-2xl font-bold text-gray-900">{totalCandidates}</p>
              </div>
              <div className="flex items-center text-green-600 text-sm font-medium">
                <ArrowUp className="w-4 h-4 mr-1" />
                +12%
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Open Roles</p>
                <p className="text-2xl font-bold text-gray-900">{openRoles}</p>
              </div>
              <div className="flex items-center text-green-600 text-sm font-medium">
                <ArrowUp className="w-4 h-4 mr-1" />
                +3
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Active Referrals</p>
                <p className="text-2xl font-bold text-gray-900">{activeReferrals}</p>
              </div>
              <div className="flex items-center text-green-600 text-sm font-medium">
                <ArrowUp className="w-4 h-4 mr-1" />
                +8%
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Referrals</p>
                <p className="text-2xl font-bold text-gray-900">{totalCandidates}</p>
              </div>
              <div className="flex items-center text-green-600 text-sm font-medium">
                <ArrowUp className="w-4 h-4 mr-1" />
                +$2,100
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11" onClick={() => navigate('/manage-roles')}>
              Post New Role
            </Button>
            <Button variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg h-11" onClick={() => navigate('/manage-roles')}>
              Review Referrals
            </Button>
          </CardContent>
        </Card>

        {/* Open Roles Progress */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Open Roles Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {roleProgress.length > 0 ? roleProgress.map(role => <div key={role.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{role.title}</p>
                    <span className="text-sm text-gray-500">{role.applications} referrals</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Progress value={role.percentage} className="flex-1" />
                    <span className="text-sm font-medium text-gray-900 min-w-[40px]">
                      {role.percentage}%
                    </span>
                  </div>
                </div>) : <p className="text-gray-500 text-center py-4">No active roles</p>}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentActivity.length > 0 ? recentActivity.map(activity => (
              <div key={activity.id} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50">
                <div 
                  className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getStatusColor(activity.status)}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 font-medium">
                    {activity.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {activity.time}
                  </p>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>;
};
export default HRDashboard;